import React, { useEffect, useState } from "react";
import { Button, Flex, Input, message } from "antd";
import {
  CheckCircleOutlined,
  CheckOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  ExportOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  ReloadOutlined,
  UpOutlined,
} from "@ant-design/icons";

type SimpleRowAction = {
  id: string;
  type: string;
  label: string;
  icon: string;
  visibilityRules: string;
};

const RowActionConfig: React.FC = () => {
  const generateId = () =>
    Date.now().toString() + Math.random().toString(36).substring(2, 6); // NOSONAR: Non-secure random number generator is safe for generating temporary IDs

  const [items, setItems] = useState<SimpleRowAction[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [jsonInput, setJsonInput] = useState("");

  const selected = items.find((i) => i.id === selectedId) || null;

  const loadJson = () => {
    try {
      const parsed = JSON.parse(jsonInput);
      if (!Array.isArray(parsed)) throw new Error("Expected an array");
      const withIds = parsed.map((item: any) => ({
        id: generateId(),
        type: item.type ?? "",
        label: item.label ?? "",
        icon: item.icon ?? "",
        visibilityRules: item.visibilityRules ?? "",
      }));
      setItems(withIds);
      setSelectedId(null);
    } catch (err: any) {
      message.error("Invalid JSON format: " + err.message);
    }
  };

  const updateSelected = (updated: SimpleRowAction) => {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };

  const addItem = () => {
    const newItem: SimpleRowAction = {
      id: generateId(),
      type: "",
      label: "",
      icon: "",
      visibilityRules: "",
    };
    setItems([...items, newItem]);
    setSelectedId(newItem.id);
  };

  const deleteItem = (id: string) => {
    setItems((prev) => prev.filter((i) => i.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  // validate JSON
  const validateJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      message.success("JSON is correct format!");
      return JSON.stringify(parsed, null, 2); // Pretty print with 2 spaces
    } catch (e) {
      message.error("Invalid JSON format");
      console.error("Invalid JSON format", e);
      return json; // Return original if parsing fails
    }
  };

  // Move item up
  const moveItemUp = (id: string) => {
    const index = items.findIndex((item) => item.id === id);
    if (index > 0) {
      const newItems = [...items];
      const [movedItem] = newItems.splice(index, 1);
      newItems.splice(index - 1, 0, movedItem);
      setItems(newItems);
    }
  };

  // Move item down
  const moveItemDown = (id: string) => {
    const index = items.findIndex((item) => item.id === id);
    if (index < items.length - 1) {
      const newItems = [...items];
      const [movedItem] = newItems.splice(index, 1);
      newItems.splice(index + 1, 0, movedItem);
      setItems(newItems);
    }
  };

  // Update Query JSON
  const updateQueryJsonInput = () => {
    try {
      const parseddata = JSON.parse(jsonInput);
      const stringified = JSON.stringify(parseddata);
      const query = `Update Report SET RowActionConfig = '${stringified}' Where Id = ??`;
      navigator.clipboard
        .writeText(query)
        .then(() => {
          message.success("JSON copied to clipboard!");
        })
        .catch(() => {
          message.error("Failed to copy text!");
        });
    } catch (e) {
      message.error("Invalid JSON format");
      console.error("Invalid JSON format", e);
      return jsonInput; // Return original if parsing fails
    }
  };

  // beautify JSON
  const beautifyJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      return JSON.stringify(parsed, null, 2); // Pretty print with 2 spaces
    } catch (e) {
      message.error("Invalid JSON format for beautification");
      console.error("Invalid JSON format for beautification", e);
      return json; // Return original if parsing fails
    }
  };

  // minify JSON
  const minifyJson = (json: string) => {
    try {
      const parsed = JSON.parse(json);
      return JSON.stringify(parsed); // Minified JSON
    } catch (e) {
      message.error("Invalid JSON format for minification");
      console.error("Invalid JSON format for minification", e);
      return json; // Return original if parsing fails
    }
  };

  useEffect(() => {
    if (items.length === 0) {
      setJsonInput(""); // or setJsonInput(null); if you prefer null
    } else {
      const cleaned = items.map(({ id, ...rest }) => rest);
      setJsonInput(JSON.stringify(cleaned, null, 2));
    }
  }, [items]);

  return (
    <div className="App">
      <h3>Row action JSON builder</h3>
      <Input.TextArea
        rows={6}
        placeholder="Paste JSON here..."
        value={jsonInput}
        onChange={(e) => setJsonInput(e.target.value)}
        style={{ resize: "none", width: "100%" }}
      />

      <div style={{ marginTop: 20, marginBottom: 8 }}>
        <Flex vertical gap="small">
          <Flex gap="small" wrap>
            <Button
              onClick={loadJson}
              color="primary"
              variant="solid"
              icon={<ReloadOutlined />}
            >
              Load JSON
            </Button>

            <Button
              onClick={() => {
                try {
                  const parseddata = JSON.parse(jsonInput);
                  console.log(parseddata);
                  navigator.clipboard.writeText(jsonInput);
                  message.success("JSON copied to clipboard!");
                } catch (e) {
                  message.error("Invalid JSON format");
                  console.error("Invalid JSON format", e);
                  return jsonInput;
                }
              }}
              color="pink"
              variant="solid"
              icon={<CopyOutlined />}
            >
              Copy JSON
            </Button>

            <Button
              onClick={() => {
                const beautified = beautifyJson(jsonInput);
                setJsonInput(beautified);
                message.success("Beautified JSON Done!");
              }}
              color="purple"
              variant="solid"
              icon={<ExportOutlined />}
            >
              Beautify
            </Button>

            <Button
              onClick={() => {
                const minified = minifyJson(jsonInput);
                setJsonInput(minified);
                message.success("Minified JSON Done!");
              }}
              color="orange"
              variant="solid"
              icon={<MinusCircleOutlined />}
            >
              Minify
            </Button>

            <Button
              onClick={() => {
                const validateJsondata = validateJson(jsonInput);
                setJsonInput(validateJsondata);
              }}
              color="danger"
              variant="solid"
              icon={<CheckCircleOutlined />}
            >
              Validate JSON{" "}
            </Button>

            <Button
              onClick={updateQueryJsonInput}
              color="cyan"
              variant="solid"
              icon={<CheckOutlined />}
            >
              Copy JSON Query
            </Button>
          </Flex>
        </Flex>
      </div>

      <h3>Row Actions</h3>

      {items.map((item) => (
        <div
          key={item.id}
          style={{ marginBottom: 8, padding: 6, border: "1px solid #ccc" }}
        >
          <Flex gap="small" wrap>
            <b>{item.label || "Untitled"}</b> [{item.type}]
            <Button
              onClick={() => setSelectedId(item.id)}
              color="primary"
              variant="dashed"
              size="small"
              icon={<EditOutlined />}
            />
            <Button
              onClick={() => deleteItem(item.id)}
              color="danger"
              variant="dashed"
              size="small"
              icon={<DeleteOutlined />}
            />
            <Button
              onClick={() => moveItemUp(item.id)}
              color="geekblue"
              variant="dashed"
              size="small"
              icon={<UpOutlined />}
            />
            <Button
              onClick={() => moveItemDown(item.id)}
              color="magenta"
              variant="dashed"
              size="small"
              icon={<DownOutlined />}
            />
          </Flex>
        </div>
      ))}

      <Button
        onClick={addItem}
        color="primary"
        variant="solid"
        icon={<PlusCircleOutlined />}
      >
        Add New Row Action
      </Button>

      {selected && (
        <div style={{ marginTop: 20 }}>
          <h3>Edit Row Action</h3>
          <table className="table table-bordered">
            <thead style={{ display: "none" }}>
              <tr>
                <th scope="col"></th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Label Name</td>
                <td>
                  <Input
                    value={selected.label}
                    onChange={(e) =>
                      updateSelected({ ...selected, label: e.target.value })
                    }
                    placeholder="Label name to be visible on screen"
                  />
                </td>
              </tr>
              <tr>
                <td>Action Type</td>
                <td>
                  <Input
                    value={selected.type}
                    onChange={(e) =>
                      updateSelected({ ...selected, type: e.target.value })
                    }
                    placeholder="System Defined Action Types: DynamicReportActionExportToExcel/DynamicMasterRowEdit"
                  />
                  <span>
                    <strong>Note:</strong> System Defined Action Types:
                    DynamicReportActionExportToExcel/DynamicMasterRowEdit
                  </span>
                </td>
              </tr>
              <tr>
                <td>Icon Name</td>
                <td>
                  <Input
                    value={selected.icon}
                    onChange={(e) =>
                      updateSelected({ ...selected, icon: e.target.value })
                    }
                    placeholder="Eg - EditOutlined/HistoryOutlined"
                  />
                  <span>
                    <strong>Refer:</strong>{" "}
                    <a
                      href="https://ant.design/components/icon"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      List of icons
                    </a>
                  </span>
                </td>
              </tr>
              <tr>
                <td>Visibility Rules</td>
                <td>
                  <Input
                    value={selected.visibilityRules}
                    onChange={(e) =>
                      updateSelected({
                        ...selected,
                        visibilityRules: e.target.value,
                      })
                    }
                    placeholder="Eg - viewClick"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RowActionConfig;
